<template>
  <div class="min-h-screen bg-gray-900">
    <!-- Header -->
    <div class="bg-gradient-to-r from-gray-800 to-gray-900 text-white py-16">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 class="text-4xl font-bold text-white mb-4">{{ $t('imprint.title') }}</h1>
        <p class="text-xl text-gray-300">{{ $t('imprint.subtitle') }}</p>
      </div>
    </div>

    <!-- Content -->
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div class="prose prose-lg max-w-none">
        <!-- Service Provider Information -->
        <section class="mb-12">
                      <h2 class="text-2xl font-bold text-white mb-4">{{ $t('imprint.provider.title') }}</h2>
          <div class="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <p class="text-gray-300 mb-2"><strong>{{ $t('imprint.provider.name') }}:</strong> Heidelbell</p>
            <p class="text-gray-300 mb-2"><strong>{{ $t('imprint.provider.address') }}:</strong> Jahnstrasse 32</p>
            <p class="text-gray-300 mb-2"><strong>{{ $t('imprint.provider.city') }}:</strong> D-69120 Heidelberg</p>
            <p class="text-gray-300 mb-2"><strong>{{ $t('imprint.provider.country') }}:</strong> Germany</p>
          </div>
        </section>

        <!-- Contact Information -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('imprint.contact.title') }}</h2>
          <div class="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <p class="text-gray-300 mb-2"><strong>{{ $t('imprint.contact.email') }}:</strong> connect@heidelbell.com</p>
            <p class="text-gray-300 mb-2"><strong>{{ $t('imprint.contact.phone') }}:</strong> {{ $t('contact.info.phoneValue') }}</p>
            <p class="text-gray-300"><strong>{{ $t('imprint.contact.website') }}:</strong> https://heidelbell.com</p>
          </div>
        </section>

        <!-- Legal Representatives -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('imprint.representatives.title') }}</h2>
          <div class="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <p class="text-gray-300 mb-4">{{ $t('imprint.representatives.content') }}</p>
          </div>
        </section>

        <!-- Commercial Register -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('imprint.register.title') }}</h2>
          <div class="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <p class="text-gray-300 mb-2"><strong>{{ $t('imprint.register.court') }}:</strong> {{ $t('imprint.register.courtValue') }}</p>
            <p class="text-gray-300"><strong>{{ $t('imprint.register.number') }}:</strong> {{ $t('imprint.register.numberValue') }}</p>
          </div>
        </section>

        <!-- VAT Information -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('imprint.vat.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('imprint.vat.content') }}</p>
        </section>

        <!-- Professional Associations -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('imprint.professional.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('imprint.professional.content') }}</p>
        </section>

        <!-- Content Responsibility -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('imprint.responsibility.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('imprint.responsibility.content') }}</p>
        </section>

        <!-- Copyright -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('imprint.copyright.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('imprint.copyright.content') }}</p>
        </section>

        <!-- External Links -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('imprint.links.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('imprint.links.content') }}</p>
        </section>

        <!-- Disclaimer -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('imprint.disclaimer.title') }}</h2>
          
          <!-- Content Liability -->
          <div class="mb-6">
            <h3 class="text-xl font-semibold text-gray-200 mb-3">{{ $t('imprint.disclaimer.contentLiability.title') }}</h3>
            <p class="text-gray-300 mb-4">{{ $t('imprint.disclaimer.contentLiability.content') }}</p>
          </div>
          
          <!-- Link Liability -->
          <div class="mb-6">
            <h3 class="text-xl font-semibold text-gray-200 mb-3">{{ $t('imprint.disclaimer.linkLiability.title') }}</h3>
            <p class="text-gray-300 mb-4">{{ $t('imprint.disclaimer.linkLiability.content') }}</p>
          </div>
        </section>

        <!-- Data Protection -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('imprint.dataProtection.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('imprint.dataProtection.content') }}</p>
          <router-link to="/privacy" class="text-primary underline">
            {{ $t('imprint.dataProtection.link') }}
          </router-link>
        </section>

        <!-- Technical Information -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('imprint.technical.title') }}</h2>
          <div class="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <p class="text-gray-300 mb-2"><strong>{{ $t('imprint.technical.hosting') }}:</strong> {{ $t('imprint.technical.hostingValue') }}</p>
            <p class="text-gray-300 mb-2"><strong>{{ $t('imprint.technical.framework') }}:</strong> Vue.js 3</p>
            <p class="text-gray-300 mb-2"><strong>{{ $t('imprint.technical.styling') }}:</strong> Tailwind CSS</p>
            <p class="text-gray-300"><strong>{{ $t('imprint.technical.build') }}:</strong> Vite</p>
          </div>
        </section>

        <!-- Generator Note -->
        <section class="mb-12">
          <p class="text-gray-400 text-sm italic">{{ $t('imprint.generatorNote.content') }}</p>
        </section>

        <!-- Last Updated -->
        <section class="mb-12">
          <div class="bg-blue-50 border-l-4 border-blue-400 p-4">
            <p class="text-blue-700">
              <strong>{{ $t('imprint.lastUpdated') }}:</strong> {{ buildDate }}
            </p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { useBuildDate } from '../composables/useBuildDate'

const { t } = useI18n()
const { buildDate } = useBuildDate()

// SEO Meta tags
document.title = t('imprint.seo.title')
document.querySelector('meta[name="description"]')?.setAttribute('content', t('imprint.seo.description'))
</script>

<style scoped>
.prose {
  @apply text-gray-300 leading-relaxed text-justify;
}

.prose h2 {
  @apply text-2xl font-bold text-white mb-4 mt-8;
}

.prose h3 {
  @apply text-xl font-semibold text-white mb-3 mt-6;
}

.prose h4 {
  @apply text-lg font-semibold text-white mb-2 mt-4;
}

.prose p {
  @apply mb-4;
}

.prose ul {
  @apply mb-6;
}

.prose li {
  @apply mb-2;
}
</style> 